import {
  Box,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  Select,
  TextField,
  Button,
  Typography
} from '@mui/material';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { api } from '../../api';
import { ActiveStatus, CovidApplication } from '../../schema/ptt';

interface CovidApplicationProps {
  person_id?: string | number;
  editable?: boolean;
  reloadStatus?: () => void;
}

const CovidApplicationPage: React.FC<CovidApplicationProps> = ({
  person_id,
  editable,
  reloadStatus
}: CovidApplicationProps) => {
  const [activeStatus, setActiveStatus] = useState<ActiveStatus | null>(null);
  const [cva, setCVA] = useState<CovidApplication | null>(null);
  const [isOpenSuccessDialog, setIsOpenSuccessDialog] = useState<boolean>(false);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const openSuccessDialog = () => {
    setIsOpenSuccessDialog(true);
    setTimeout(() => {
      setIsOpenSuccessDialog(false);
    }, 1000);
  };

  const saveTempStatus = () => {
    if (!editable) {
      return;
    }
    if (isLoading) {
      return;
    }
    if (activeStatus) {
      setIsLoading(true);
      api
        .post(`person/${person_id}/temporary_status`, {
          is_cva_active: activeStatus.is_temp_active,
          cva_comment: activeStatus.comment
        })
        .then(() => {
          openSuccessDialog();
          reloadStatus && reloadStatus();
        })
        .finally(() => setIsLoading(false));
    }
  };

  useEffect(() => {
    if (person_id) {
      api.get(`/person/${person_id}/cva`).then((res) => {
        const { data } = res;
        setCVA(data);
      });
      api.get(`/person/${person_id}/cva/status`).then((res) => {
        const { data } = res;
        setActiveStatus(data);
      });
    }
  }, [person_id]);
  return (
    <>
      {cva == null ? (
        <Typography variant="h6" mt={3} sx={{ color: "red" }}>
          ไม่พบข้อมูล Covid Application หรือไม่ได้อยู้ในช่วงเวลารับรองผล
          <Typography variant="subtitle2" mt={1} sx={{ color: "black" }}>
              กรุณาตรวจสอบข้อมูลผ่านระบบ Covid Application
            </Typography>
        </Typography>
      ) : (
        <>
          <Typography variant="h6">การอนุญาต Covid Application</Typography>
          {activeStatus?.is_active ? (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={3}>
                  <TextField
                    fullWidth
                    label="สถานะการอนุญาต"
                    variant="outlined"
                    placeholder="สถานะการอนุญาต"
                    value={'ผ่านโดยระบบ'}
                    autoComplete="off"
                    InputProps={{
                      readOnly: true
                    }}
                  />
                </Grid>
              </Grid>
            </Box>
          ) : (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={3}>
                  {editable ? (
                    <FormControl fullWidth>
                      <InputLabel id="is_active">สถานะการอนุญาตชั่วคราว</InputLabel>
                      <Select
                        labelId="is_active"
                        label="สถานะการอนุญาตชั่วคราว"
                        variant="outlined"
                        onChange={(e) =>
                          activeStatus
                            ? setActiveStatus({
                                ...activeStatus,
                                is_temp_active: e.target.value == 'true'
                              })
                            : null
                        }
                        value={`${activeStatus?.is_temp_active === true}`}
                        autoComplete="off"
                        disabled={!activeStatus}
                      >
                        <MenuItem value={'true'}>ผ่าน</MenuItem>
                        <MenuItem value={'false'}>ไม่ผ่าน</MenuItem>
                      </Select>
                    </FormControl>
                  ) : (
                    <TextField
                      fullWidth
                      label="สถานะการอนุญาต"
                      variant="outlined"
                      placeholder="สถานะการอนุญาต"
                      value={activeStatus?.is_temp_active ? 'ผ่าน' : 'ไม่ผ่าน'}
                      autoComplete="off"
                      InputProps={{
                        readOnly: true
                      }}
                    />
                  )}
                </Grid>
                <Grid item xs={9}>
                  <TextField
                    fullWidth
                    label="หมายเหตุการอนุญาตชั่วคราวโดยเจ้าหน้าที่"
                    variant="outlined"
                    placeholder="หมายเหตุการอนุญาตชั่วคราวโดยเจ้าหน้าที่"
                    onChange={(e) =>
                      activeStatus
                        ? setActiveStatus({ ...activeStatus, comment: e.target.value })
                        : null
                    }
                    value={activeStatus?.comment ? activeStatus.comment : ''}
                    autoComplete="off"
                    disabled={!activeStatus}
                  />
                </Grid>
              </Grid>
              {editable ? (
                <Grid container columns={2} spacing={2} sx={{ mt: 0 }} direction={'row-reverse'}>
                  <Grid item>
                    <Button onClick={() => saveTempStatus()} variant="contained" color="primary">
                      บันทึก
                    </Button>
                  </Grid>
                </Grid>
              ) : null}
            </Box>
          )}
          <Typography variant="h6" mt={3}>
            Covid Application
          </Typography>
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="วันเวลาที่เริ่มรับรองผล COVID-19"
                  variant="outlined"
                  placeholder="วันเวลาที่เริ่มรับรองผล COVID-19"
                  value={
                    cva?.covid_start_datetime
                      ? dayjs(cva.covid_start_datetime).format('DD/MM/YYYY HH:mm')
                      : ''
                  }
                  InputProps={{ readOnly: true }}
                  sx={{
                    input: {
                      color: dayjs(cva.covid_start_datetime) > dayjs() ? 'red' : ''
                    }
                  }}
                  disabled={!cva?.covid_start_datetime}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="วันเวลาที่สิ้นสุดการรับรองผล COVID-19"
                  variant="outlined"
                  placeholder="วันเวลาที่สิ้นสุดการรับรองผล COVID-19"
                  value={
                    cva?.covid_end_datetime
                      ? dayjs(cva.covid_end_datetime).format('DD/MM/YYYY HH:mm')
                      : ''
                  }
                  sx={{
                    input: {
                      color: dayjs(cva.covid_end_datetime) < dayjs() ? 'red' : ''
                    }
                  }}
                  InputProps={{ readOnly: true }}
                  disabled={!cva?.covid_end_datetime}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="ผลการตรวจ COVID-19"
                  variant="outlined"
                  placeholder="ผลการตรวจ COVID-19"
                  value={
                    cva?.covid_test_result != undefined
                      ? cva.covid_test_result
                        ? 'ผ่าน'
                        : 'ไม่ผ่าน'
                      : ''
                  }
                  InputProps={{ readOnly: true }}
                  disabled={cva?.covid_test_result == undefined}
                />
              </Grid>
            </Grid>
          </Box>
        </>
      )}
      <Dialog open={isOpenSuccessDialog} onClose={() => setIsOpenSuccessDialog(false)}>
        <DialogTitle>{'Success'}</DialogTitle>
        <DialogContent>
          <DialogContentText>อัพเดทข้อมูลการตรวจสอบและอัปเดตบุคคลสำเร็จ</DialogContentText>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CovidApplicationPage;
